import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { addDevices } from "../../service/gatewayService";
import { Patterns, GATEWAY_NAME_MESSAGE } from "constant";
import { GATEWAY_ROUTES } from "constant/gateway";
import { isHttpSuccess, isValidValueInForm } from "utils/functions";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { showSuccessAlert, showErrorAlert, getAPIError } from "utils/alert";

const formConfig: any = {
    uuid: {
        def: "",
        regex: Patterns.uuidPattern,
        errorMessage: "Gateway UUID must be 19 characters long.",
    },
    serialNumber: {
        def: "",
        regex: Patterns.serialPattern,
        errorMessage: "Serial Number must be 8 characters long.",
    },
    macAddress: {
        def: "",
        regex: Patterns.macAddressPattern,
        errorMessage: "Ethernet MAC Address must follow XX:XX:XX:XX:XX:XX.",
    },
    gatewayName: {
        def: "",
        regex: Patterns.gatewayNamePattern,
        errorMessage: GATEWAY_NAME_MESSAGE,
    },
};

const defaultFormData = Object.keys(formConfig).reduce(
    (dict: any, k: string) => {
        dict[k] = { value: formConfig[k].def, isError: false };
        return dict;
    },
    {}
);

type FormDataValue<T> = {
    value: T;
    isError: boolean;
};

type AddGWFormData = {
    uuid: FormDataValue<string>;
    serialNumber: FormDataValue<string>;
    macAddress: FormDataValue<string>;
    gatewayName: FormDataValue<string>;
};

const AddGateWay = () => {
    const history = useHistory();
    const [formData, setFormData] = useState<AddGWFormData>(defaultFormData);

    const isInvalidForm = Object.keys(formData).some((k) => {
        const key = k as keyof AddGWFormData;
        const isInvalid = !isValidValueInForm(
            "value",
            formData[key],
            defaultFormData[key],
            formConfig[key].regex
        );
        return isInvalid;
    });

    const handleFormDataChange = (
        field: keyof AddGWFormData,
        value: string
    ) => {
        const fieldData = { value, isError: false };
        const isError = !isValidValueInForm(
            "value",
            fieldData,
            defaultFormData[field],
            formConfig[field].regex
        );

        fieldData.isError = isError;

        setFormData((prev) => ({
            ...prev,
            [field]: fieldData,
        }));
    };

    const handleSaveGatewayToApi = async () => {
        const response: any = await addDevices(
            formData.uuid.value.trim(),
            formData.serialNumber.value.trim(),
            formData.macAddress.value.trim(),
            formData.gatewayName.value.trim()
        );

        if (isHttpSuccess(response.status)) {
            showSuccessAlert({
                message:
                    response.message ||
                    "New Gateway has been added successfully.",
            });
            history.push(GATEWAY_ROUTES.GATEWAYS_ALL);
        } else {
            showErrorAlert(
                getAPIError(response, "Adding a new gateway has failed.")
            );
        }
    };

    return (
        <ContentWrapper>
            <div className="page-content">
                <Container>
                    <Row>
                        <Col sm="12">
                            <h5 className="page-title">Add Gateway</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <Form className="d-inline-block w-100">
                                <div className="form-box">
                                    <Form.Group className="mb-4">
                                        <Form.Label>UUID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            required
                                            name="uuid"
                                            aria-label="uuid"
                                            isInvalid={formData.uuid.isError}
                                            onChange={(e) => {
                                                handleFormDataChange(
                                                    "uuid",
                                                    e.target.value
                                                );
                                            }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formConfig.uuid.errorMessage}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-4">
                                        <Form.Label>Serial Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            required
                                            name="serialNumber"
                                            aria-label="serial-number"
                                            isInvalid={
                                                formData.serialNumber.isError
                                            }
                                            onChange={(e) => {
                                                handleFormDataChange(
                                                    "serialNumber",
                                                    e.target.value
                                                );
                                            }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {
                                                formConfig.serialNumber
                                                    .errorMessage
                                            }
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Ethernet MAC Address
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            required
                                            name="macAddress"
                                            aria-label="mac-address"
                                            isInvalid={
                                                formData.macAddress.isError
                                            }
                                            onChange={(e) => {
                                                handleFormDataChange(
                                                    "macAddress",
                                                    e.target.value
                                                );
                                            }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formConfig.macAddress.errorMessage}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-4">
                                        <Form.Label>Gateway Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            required
                                            name="gatewayName"
                                            aria-label="name-gateway"
                                            isInvalid={
                                                formData.gatewayName.isError
                                            }
                                            onChange={(e) => {
                                                handleFormDataChange(
                                                    "gatewayName",
                                                    e.target.value
                                                );
                                            }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {
                                                formConfig.gatewayName
                                                    .errorMessage
                                            }
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                            </Form>
                            <Button
                                variant="secondary"
                                className="mt-4 mr-2"
                                onClick={() => {
                                    history.goBack();
                                }}
                            >
                                CANCEL
                            </Button>
                            <Button
                                variant="primary"
                                className="mt-4"
                                onClick={handleSaveGatewayToApi}
                                disabled={isInvalidForm}
                            >
                                SAVE
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default AddGateWay;
